import { useEffect, useRef } from "react";
import { AREAS, MAX_SCORE, heatRgb, fmtScore } from "@/lib/heat";

const LOGO_W = 1890;
const LOGO_H = 1916;
const CX = 931 / LOGO_W;
const CY = 1064 / LOGO_H;
const R = 1060 / LOGO_W;
const EMPTY = [226, 232, 240];

let logoPromise;
function loadLogo() {
  if (!logoPromise) {
    logoPromise = new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = "/flower-logo.png";
    });
  }
  return logoPromise;
}

const sorted = [...AREAS].sort((a, b) => a.angle - b.angle);

function scoreAt(deg, scores) {
  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i];
    const b = sorted[(i + 1) % sorted.length];
    let a0 = a.angle;
    let a1 = b.angle + (i === sorted.length - 1 ? 360 : 0);
    let d = deg;
    while (d < a0) d += 360;
    if (d <= a1) {
      const w = (d - a0) / (a1 - a0);
      const s = (w * w * w) / (w * w * w + (1 - w) ** 3);
      return (scores[a.key] || 0) * (1 - s) + (scores[b.key] || 0) * s;
    }
  }
  return 0;
}

function paint(ctx, img, size, scores, max, labels) {
  const pad = labels ? size * 0.17 : size * 0.02;
  const lw = size - pad * 2;
  const lh = lw * (LOGO_H / LOGO_W);
  const lx = pad;
  const ly = (size - lh) / 2;
  const cx = lx + CX * lw;
  const cy = ly + CY * lh;
  const rad = R * lw;
  const W = Math.ceil(size);
  const data = ctx.createImageData(W, W);
  const sc = {};
  AREAS.forEach((a) => (sc[a.key] = Math.max(0, Math.min(max, Number(scores?.[a.key]) || 0))));
  for (let y = 0; y < W; y++) {
    for (let x = 0; x < W; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy) / rad;
      const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
      const t = scoreAt(deg, sc) / max;
      const edge = Math.max(0, Math.min(1, (t - r) / 0.025 + 0.5));
      const c = heatRgb(r);
      const o = (y * W + x) * 4;
      data.data[o] = c[0] * edge + EMPTY[0] * (1 - edge);
      data.data[o + 1] = c[1] * edge + EMPTY[1] * (1 - edge);
      data.data[o + 2] = c[2] * edge + EMPTY[2] * (1 - edge);
      data.data[o + 3] = 255;
    }
  }
  ctx.putImageData(data, 0, 0);
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(img, lx, ly, lw, lh);
  ctx.globalCompositeOperation = "source-over";
  if (!labels) return;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  AREAS.forEach((a) => {
    const ang = (a.angle * Math.PI) / 180;
    const dist = rad * 1.02 + size * 0.07;
    const px = Math.max(size * 0.09, Math.min(size * 0.91, cx + Math.cos(ang) * dist));
    const py = Math.max(size * 0.05, Math.min(size * 0.95, cy + Math.sin(ang) * dist));
    ctx.fillStyle = "#1F4266";
    ctx.font = `700 ${Math.round(size * 0.042)}px Outfit, sans-serif`;
    ctx.fillText(a.label.toUpperCase(), px, py - size * 0.024);
    ctx.fillStyle = "#475569";
    ctx.font = `500 ${Math.round(size * 0.036)}px 'JetBrains Mono', monospace`;
    ctx.fillText(`${fmtScore(sc[a.key])}/${max}`, px, py + size * 0.022);
  });
}

export const FlowerHeatmap = ({ scores, max = MAX_SCORE, size = 420, labels = true, testId = "heatmap-canvas" }) => {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    Promise.all([loadLogo(), document.fonts?.ready]).then(([img]) => {
      const cv = ref.current;
      if (cancelled || !cv) return;
      const px = Math.round(size * dpr);
      cv.width = px;
      cv.height = px;
      paint(cv.getContext("2d"), img, px, scores, max, labels);
    });
    return () => {
      cancelled = true;
    };
  }, [scores, max, size, labels]);

  return (
    <canvas
      ref={ref}
      data-testid={testId}
      style={{ width: "100%", maxWidth: size, aspectRatio: "1 / 1" }}
      aria-label="Five petal heat map of scores"
    />
  );
};
